import * as Form from '@radix-ui/react-form';
import { Box, Flex, Text } from '@radix-ui/themes';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  forwardRef,
  ReactNode,
} from 'react';

type BaseProps = {
  name: string;
  label?: string;
  error?: string;
  containerClassName?: string;
  multiline?: boolean;
  compact?: boolean;
  optional?: boolean;
  rightElement?: ReactNode;
};

type InputProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseProps>;
type TextareaProps = BaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof BaseProps>;

export type FormInputProps = InputProps | (TextareaProps & { multiline: true });

export const FormInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FormInputProps
>(
  (
    {
      name,
      label,
      error,
      containerClassName,
      className,
      multiline,
      required,
      compact,
      optional,
      rightElement,
      ...props
    },
    ref,
  ) => {
    const size = compact ? 'h-[24px]' : 'h-[46px]';

    return (
      <Form.Field name={name} className={cn('relative', containerClassName)}>
        <Flex direction="column">
          {label && (
            <Form.Label>
              {label}
              {optional && (
                <Text size="2" color="gray">
                  {' '}
                  (Optional)
                </Text>
              )}
            </Form.Label>
          )}
          <Box className="relative">
            <Form.Control asChild>
              {multiline ? (
                <textarea
                  ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
                  className={cn(
                    'text-black',
                    'w-full p-2 bg-white rounded-md pointer-events-auto relative',
                    'border border-solid border-[#ced4da]',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500',
                    'min-h-[150px] resize-y',
                    error && 'border-red-500',
                    rightElement && 'pr-10',
                    className,
                  )}
                  required={required}
                  {...(props as TextareaProps)}
                />
              ) : (
                <input
                  ref={ref as React.ForwardedRef<HTMLInputElement>}
                  className={cn(
                    'text-black',
                    size,
                    'w-full p-2 bg-white rounded-md pointer-events-auto relative',
                    'border border-solid border-[#ced4da]',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500',
                    error && 'border-red-500',
                    rightElement && 'pr-10',
                    className,
                  )}
                  required={required}
                  {...(props as InputProps)}
                />
              )}
            </Form.Control>
            {rightElement && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                {rightElement}
              </div>
            )}
            {label && (
              <Form.Message
                className="text-red-500 text-xs mt-1"
                match="valueMissing"
              >
                Please enter your {label.toLowerCase()}
              </Form.Message>
            )}
            {error && (
              <Text size="1" color="red" className="mt-1">
                {error}
              </Text>
            )}
          </Box>
        </Flex>
      </Form.Field>
    );
  },
);

FormInput.displayName = 'FormInput';

export default FormInput;
